import { ref, watch, computed, nextTick } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { invalidateNavBadges } from '../ui/useNavBadges.js'

/**
 * Composable for managing the action assignment modal
 * @param {Object} options
 * @param {Ref} options.approvedProspects - Computed ref of approved prospects
 * @param {Function} options.isProspectFullyAssigned - Function to check if prospect is fully assigned
 * @param {Ref} options.isFullyLoaded - Ref indicating if all data is loaded
 */
export function useActionModal({ approvedProspects, isProspectFullyAssigned, isFullyLoaded }) {
  const queryClient = useQueryClient()

  const showActionModal = ref(false)
  const selectedProspectForAction = ref(null)
  const actionPopupShown = ref(new Set())
  const initialLoadComplete = ref(false)

  function openActionModal(prospect) {
    selectedProspectForAction.value = prospect
    showActionModal.value = true
  }

  function closeActionModal() {
    showActionModal.value = false
    selectedProspectForAction.value = null
  }

  function handleActionCreated(_action) {
    if (selectedProspectForAction.value) {
      actionPopupShown.value.add(selectedProspectForAction.value.id)
    }
    invalidateNavBadges(queryClient, { doen: true })
  }

  const actionModalProps = computed(() => ({
    open: showActionModal.value,
    prospect: selectedProspectForAction.value,
    defaultAssignee: {
      name: selectedProspectForAction.value?.submitted_by_name || '',
      email: selectedProspectForAction.value?.submitted_by_email || ''
    }
  }))

  // Mark initial load complete when fully loaded
  watch(isFullyLoaded, async loaded => {
    if (loaded && !initialLoadComplete.value) {
      for (const prospect of approvedProspects.value) {
        if (isProspectFullyAssigned(prospect) || prospect.action_status === 'completed') {
          actionPopupShown.value.add(prospect.id)
        }
      }
      await nextTick()
      initialLoadComplete.value = true
    }
  })

  // Watch for assignment completion to trigger action modal
  watch(
    () =>
      approvedProspects.value.map(p => ({ id: p.id, fullyAssigned: isProspectFullyAssigned(p) })),
    (newStates, oldStates) => {
      if (!initialLoadComplete.value) return
      if (!oldStates) return

      const oldMap = new Map(oldStates.map(s => [s.id, s.fullyAssigned]))

      for (const newState of newStates) {
        const wasFullyAssigned = oldMap.get(newState.id)
        if (
          newState.fullyAssigned &&
          !wasFullyAssigned &&
          !actionPopupShown.value.has(newState.id)
        ) {
          const prospect = approvedProspects.value.find(p => p.id === newState.id)
          if (prospect) {
            openActionModal(prospect)
            actionPopupShown.value.add(newState.id)
          }
        }
      }
    },
    { deep: true }
  )

  return {
    showActionModal,
    selectedProspectForAction,
    actionPopupShown,
    initialLoadComplete,
    openActionModal,
    closeActionModal,
    handleActionCreated,
    actionModalProps
  }
}
